import { useEffect, useMemo, useState } from "react";
import {
  Search, FileText, AlertTriangle, CheckCircle2, Eye, Download, Loader2,
  ChevronLeft, ChevronRight, X, TrendingUp, CloudDownload, PlusCircle,
} from "lucide-react";
import { useInvoiceStore, type Invoice } from "../store/useInvoiceStore";
import { useAuthStore } from "../store/useAuthStore";
import { useProjectStore } from "../store/useProjectStore";
import { API_BASE_URL } from "../lib/api";
import { FolderKanban } from "lucide-react";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

// Invoice statuses a BD can set — the linked milestone follows automatically
// (invoice is the source of truth: Completed/Released → milestone Released,
// Escrowed → In Escrow, re-opened → Pending).
const INVOICE_STATUSES = ["Upcoming", "Submitted", "Escrowed", "Released", "Completed", "Overdue"];

const money = (n: number, cur = "USD") =>
  cur === "USD" ? `$${(Math.round((n || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : `${cur} ${Math.round(n || 0).toLocaleString()}`;
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const initials = (name?: string) => (name || "C").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const PAID_STATUSES = ["Completed", "Released"];
const STATUS_STYLE: Record<string, string> = {
  Upcoming: "bg-surface-container-highest text-on-surface-variant",
  Submitted: "bg-primary-container text-primary",
  Escrowed: "bg-amber-100 text-amber-700",
  Released: "bg-secondary-container text-on-secondary-container",
  Completed: "bg-secondary-container text-on-secondary-container",
  Overdue: "bg-error-container text-error",
};

const DATE_RANGES = [
  { key: "all", label: "All Time" },
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "30", label: "Last 30 Days" },
  { key: "quarter", label: "Last Quarter" },
  { key: "year", label: "This Year" },
];
const rangeBounds = (key: string): { from?: string; to?: string } => {
  const now = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const start = (d: Date) => { d.setHours(0, 0, 0, 0); return d; };
  switch (key) {
    case "today": return { from: iso(start(new Date())) };
    case "week": { const d = start(new Date()); d.setDate(d.getDate() - d.getDay()); return { from: iso(d) }; }
    case "month": return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)) };
    case "30": { const d = start(new Date()); d.setDate(d.getDate() - 30); return { from: iso(d) }; }
    case "quarter": { const d = start(new Date()); d.setMonth(d.getMonth() - 3); return { from: iso(d) }; }
    case "year": return { from: iso(new Date(now.getFullYear(), 0, 1)) };
    default: return {};
  }
};

const PER_PAGE = 10;

export default function BDInvoicesPage() {
  const { invoices, fetchInvoices, loading } = useInvoiceStore();
  const { projects, fetchProjects } = useProjectStore();
  const { authUser } = useAuthStore();
  const canWrite = ["Admin", "Supervisor", "Sales Director"].includes(authUser?.role || "");
  const [showSummary, setShowSummary] = useState(false);
  // Deep link from a milestone's View Invoice (?search=INV-1024).
  const [search, setSearch] = useState(new URLSearchParams(window.location.search).get("search") || "");
  const [range, setRange] = useState("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [view, setView] = useState<Invoice | null>(null);

  // Unique clients across the loaded invoices, for the Client filter dropdown.
  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    invoices.forEach((i) => {
      const c: any = i.client;
      const id = c?._id || c?.name;
      if (id && !map.has(id)) map.set(id, c?.username ? `${c.name} (@${c.username})` : c?.name || "—");
    });
    return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [invoices]);

  useEffect(() => {
    const b = rangeBounds(range);
    fetchInvoices({ from: b.from, to: b.to });
  }, [fetchInvoices, range]);
  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // BD updates the invoice status → the linked milestone syncs automatically.
  const [statusBusy, setStatusBusy] = useState<string | null>(null);
  const changeStatus = async (inv: Invoice, status: string) => {
    if (status === inv.status) return;
    setStatusBusy(inv._id);
    try {
      await axiosInstance.patch(`/invoices/${inv._id}/status`, { status });
      toast.success(`${inv.invoiceNumber} → ${status}. Linked milestone updated accordingly.`);
      const b = rangeBounds(range);
      fetchInvoices({ from: b.from, to: b.to });
      fetchProjects(); // milestone/rollup states changed too
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't update the invoice status");
    } finally {
      setStatusBusy(null);
    }
  };

  // KPI cards (reference: Total / Pending & Overdue / Paid)
  const kpi = useMemo(() => {
    const clients = (list: Invoice[]) => new Set(list.map((i) => (i.client as any)?._id || (i.client as any)?.name).filter(Boolean)).size;
    const projects = (list: Invoice[]) => new Set(list.map((i) => (i.project as any)?._id || (i.project as any)?.name).filter(Boolean)).size;
    const sum = (list: Invoice[]) => list.reduce((s, i) => s + (i.amount || 0), 0);
    const paid = invoices.filter((i) => PAID_STATUSES.includes(i.status));
    const overdue = invoices.filter((i) => i.status === "Overdue");
    const pending = invoices.filter((i) => !PAID_STATUSES.includes(i.status) && i.status !== "Overdue");
    // Last-6-months collected series for the Paid sparkline + month-over-month growth.
    const now = new Date();
    const series: number[] = Array.from({ length: 6 }, (_, k) => {
      const m = new Date(now.getFullYear(), now.getMonth() - (5 - k), 1);
      const next = new Date(now.getFullYear(), now.getMonth() - (5 - k) + 1, 1);
      return paid.filter((i) => {
        const d = new Date((i as any).paidAt || i.updatedAt || i.createdAt);
        return d >= m && d < next;
      }).reduce((s, i) => s + (i.amount || 0), 0);
    });
    const prev = series[4] || 0, last = series[5] || 0;
    const growth = prev > 0 ? Math.round(((last - prev) / prev) * 100) : 0;
    return {
      total: { count: invoices.length, value: sum(invoices), clients: clients(invoices), projects: projects(invoices), collectionRate: invoices.length ? Math.round((paid.length / invoices.length) * 100) : 0 },
      outstanding: { count: overdue.length + pending.length, value: sum(overdue) + sum(pending), overdue: overdue.length, pending: pending.length, clients: clients([...overdue, ...pending]), projects: projects([...overdue, ...pending]) },
      paid: { count: paid.length, value: sum(paid), clients: clients(paid), projects: projects(paid), series, growth },
    };
  }, [invoices]);

  const filtered = useMemo(() => {
    let rows = [...invoices];
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((i) => {
      const c: any = i.client, p: any = i.project;
      return [i.invoiceNumber, c?.name, c?.username, p?.name, i.milestoneTitle].some((v) => String(v || "").toLowerCase().includes(q));
    });
    if (statusFilter !== "all") rows = statusFilter === "outstanding"
      ? rows.filter((i) => !PAID_STATUSES.includes(i.status))
      : statusFilter === "paid" ? rows.filter((i) => PAID_STATUSES.includes(i.status))
      : rows.filter((i) => i.status === statusFilter);
    if (typeFilter !== "all") rows = rows.filter((i) => (i as any).projectSource === typeFilter);
    if (clientFilter !== "all") rows = rows.filter((i) => { const c: any = i.client; return (c?._id || c?.name) === clientFilter; });
    rows.sort((a, b) => {
      if (sort === "amount") return (b.amount || 0) - (a.amount || 0);
      if (sort === "due") return new Date(a.dueDate || 0).getTime() - new Date(b.dueDate || 0).getTime();
      if (sort === "oldest") return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return rows;
  }, [invoices, search, statusFilter, typeFilter, clientFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageRows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  useEffect(() => { setPage(1); }, [search, statusFilter, typeFilter, clientFilter, range]);

  const download = (id: string) => window.open(`${API_BASE_URL}/invoices/${id}/print`, "_blank");
  const exportCsv = () => {
    const b = rangeBounds(range);
    const p = new URLSearchParams();
    if (b.from) p.set("from", b.from); if (b.to) p.set("to", b.to);
    if (statusFilter !== "all" && !["outstanding", "paid"].includes(statusFilter)) p.set("status", statusFilter);
    if (typeFilter !== "all") p.set("projectType", typeFilter);
    window.open(`${API_BASE_URL}/invoices/export?${p.toString()}`, "_blank");
  };

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-on-surface">Invoices</h1>
          <p className="text-sm text-on-surface-variant mt-1">Monitor invoices, payment progress, and outstanding balances for your projects.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button onClick={() => setShowSummary(true)} className="inline-flex items-center gap-2 border border-outline-variant bg-white text-on-surface px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-surface-container-low">
            <FolderKanban size={18} /> Project Summary
          </button>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 border border-outline-variant bg-white text-on-surface px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-surface-container-low">
            <CloudDownload size={18} /> Export
          </button>
          {canWrite && (
            <button className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#004b9e]">
              <PlusCircle size={18} /> Create Invoice
            </button>
          )}
        </div>
      </div>

      {/* Search + filters bar */}
      <div className="bg-white rounded-xl border border-outline-variant shadow-sm p-3 mb-6 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-outline" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by invoice, client, username, project, or milestone..." className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-background border border-outline-variant text-sm outline-none focus:border-primary" />
        </div>
        <select value={range} onChange={(e) => setRange(e.target.value)} className="px-3 py-2.5 rounded-lg border border-outline-variant text-sm bg-white">
          {DATE_RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2.5 rounded-lg border border-outline-variant text-sm bg-white">
          <option value="all">All Statuses</option>
          <option value="outstanding">Outstanding</option>
          <option value="paid">Paid</option>
          {["Upcoming", "Submitted", "Escrowed", "Released", "Completed", "Overdue"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2.5 rounded-lg border border-outline-variant text-sm bg-white">
          <option value="all">All Types</option>
          <option value="Direct">Direct</option>
          <option value="Upwork">Upwork</option>
        </select>
        <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="px-3 py-2.5 rounded-lg border border-outline-variant text-sm bg-white max-w-[200px]">
          <option value="all">All Clients</option>
          {clientOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2.5 rounded-lg border border-outline-variant text-sm bg-white">
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="due">Due Date</option>
          <option value="amount">Invoice Amount</option>
        </select>
      </div>

      {/* KPI cards (reference layout) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Total Invoices */}
        <button onClick={() => setStatusFilter("all")} className={`text-left bg-white rounded-xl border shadow-sm p-6 relative overflow-hidden group transition-all ${statusFilter === "all" ? "border-primary ring-2 ring-primary/20" : "border-outline-variant hover:shadow-md"}`}>
          <FileText size={64} className="absolute top-2 right-2 text-primary opacity-[0.07] group-hover:opacity-[0.14] transition-opacity" />
          <div className="flex items-center gap-2 text-on-surface-variant text-sm font-semibold mb-4"><FileText size={18} className="text-primary" /> Total Invoices</div>
          <div className="flex items-end justify-between">
            <div>
              <div className="flex items-baseline gap-2"><span className="text-[30px] font-bold text-on-surface leading-none">{kpi.total.count}</span><span className="text-sm text-on-surface-variant">Total</span></div>
              <div className="text-lg font-bold text-primary mt-1">{money(kpi.total.value)}</div>
            </div>
            <div className="text-right"><p className="text-[11px] text-on-surface-variant">Across {kpi.total.clients} Clients</p></div>
          </div>
          <div className="mt-4 pt-4 border-t border-outline-variant/40 flex justify-between items-center">
            <span className="text-[11px] font-medium text-outline">Active Projects: {kpi.total.projects}</span>
            <span className="w-24 h-1.5 bg-surface-container-high rounded-full overflow-hidden"><span className="block h-full bg-primary" style={{ width: `${kpi.total.collectionRate}%` }} /></span>
          </div>
        </button>

        {/* Pending & Overdue */}
        <button onClick={() => setStatusFilter("outstanding")} className={`text-left bg-white rounded-xl border shadow-sm p-6 transition-all ${statusFilter === "outstanding" ? "border-primary ring-2 ring-primary/20" : "border-outline-variant hover:shadow-md"}`}>
          <div className="flex items-center gap-2 text-on-surface-variant text-sm font-semibold mb-4"><AlertTriangle size={18} className="text-error" /> Pending & Overdue</div>
          <div className="flex items-end justify-between">
            <div>
              <div className="flex items-baseline gap-2"><span className="text-[30px] font-bold text-on-surface leading-none">{kpi.outstanding.count}</span><span className="text-sm text-on-surface-variant">Outstanding</span></div>
              <div className="text-lg font-bold text-error mt-1">{money(kpi.outstanding.value)}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="px-2 py-0.5 bg-error-container text-error rounded text-[10px] font-bold uppercase">{kpi.outstanding.overdue} Overdue</span>
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-bold uppercase">{kpi.outstanding.pending} Pending</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-outline-variant/40">
            <div className="flex h-2 w-full rounded-full overflow-hidden bg-surface-container-high">
              <div className="h-full bg-error" style={{ width: `${kpi.outstanding.count ? (kpi.outstanding.overdue / kpi.outstanding.count) * 100 : 0}%` }} />
              <div className="h-full bg-orange-400" style={{ width: `${kpi.outstanding.count ? (kpi.outstanding.pending / kpi.outstanding.count) * 100 : 0}%` }} />
            </div>
            <p className="mt-2 text-[11px] text-on-surface-variant">{kpi.outstanding.clients} Clients • {kpi.outstanding.projects} Projects impacted</p>
          </div>
        </button>

        {/* Paid */}
        <button onClick={() => setStatusFilter("paid")} className={`text-left bg-white rounded-xl border shadow-sm p-6 transition-all ${statusFilter === "paid" ? "border-primary ring-2 ring-primary/20" : "border-outline-variant hover:shadow-md"}`}>
          <div className="flex items-center gap-2 text-on-surface-variant text-sm font-semibold mb-4"><CheckCircle2 size={18} className="text-secondary" /> Paid Invoices</div>
          <div className="flex items-end justify-between">
            <div>
              <div className="flex items-baseline gap-2"><span className="text-[30px] font-bold text-on-surface leading-none">{kpi.paid.count}</span><span className="text-sm text-on-surface-variant">Collected</span></div>
              <div className="text-lg font-bold text-secondary mt-1">{money(kpi.paid.value)}</div>
            </div>
            <Sparkline data={kpi.paid.series} />
          </div>
          <div className="mt-4 pt-4 border-t border-outline-variant/40 flex justify-between items-center">
            {kpi.paid.growth !== 0 ? (
              <span className="text-[11px] font-bold text-secondary uppercase inline-flex items-center gap-1"><TrendingUp size={13} /> {kpi.paid.growth > 0 ? "+" : ""}{kpi.paid.growth}% growth</span>
            ) : <span className="text-[11px] font-medium text-outline">Collected to date</span>}
            <span className="text-[11px] text-on-surface-variant">{kpi.paid.clients} Clients • {kpi.paid.projects} Projects</span>
          </div>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-outline-variant shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-outline-variant"><h3 className="text-lg font-bold text-on-surface">Recent Invoices</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="bg-surface-container-low text-on-surface-variant text-left text-[11px] uppercase tracking-wide">
                <th className="p-3 font-semibold">Invoice #</th>
                <th className="p-3 font-semibold">Client / User</th>
                <th className="p-3 font-semibold">Project / Milestone</th>
                <th className="p-3 font-semibold">Type</th>
                <th className="p-3 font-semibold text-right">Amount</th>
                <th className="p-3 font-semibold">Payment</th>
                <th className="p-3 font-semibold">Due</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-10 text-center"><Loader2 className="animate-spin text-primary inline" size={22} /></td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={9} className="p-10 text-center text-on-surface-variant">No invoices found.</td></tr>
              ) : pageRows.map((inv) => {
                const c: any = inv.client, p: any = inv.project;
                return (
                  <tr key={inv._id} className="border-b border-outline-variant last:border-0 hover:bg-surface-container-low/50">
                    <td className="p-3 font-bold text-on-surface">{inv.invoiceNumber}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary-container text-primary grid place-items-center text-[11px] font-bold shrink-0">{initials(c?.name)}</div>
                        <div className="min-w-0"><div className="font-semibold text-on-surface truncate">{c?.name || "—"}</div>{c?.username && <div className="text-[11px] text-on-surface-variant">@{c.username}</div>}</div>
                      </div>
                    </td>
                    <td className="p-3"><div className="text-on-surface">{p?.name || "—"}</div>{inv.milestoneTitle && <div className="text-[11px] text-on-surface-variant">{inv.milestoneTitle}</div>}</td>
                    <td className="p-3"><span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-surface-container-highest text-on-surface-variant">{(inv as any).projectSource || "Direct"}</span></td>
                    <td className="p-3 text-right font-bold text-on-surface">{money(inv.amount, inv.currency)}</td>
                    <td className="p-3 text-on-surface-variant">{inv.paymentMethod || "—"}</td>
                    <td className="p-3 text-on-surface-variant">{fmtDate(inv.dueDate)}</td>
                    <td className="p-3">
                      {/* Editable — changing the invoice status drives the milestone */}
                      <select
                        value={inv.status}
                        disabled={statusBusy === inv._id}
                        onChange={(e) => changeStatus(inv, e.target.value)}
                        title="Update the invoice status — the linked milestone follows automatically"
                        className={`text-[11px] font-bold px-2 py-1 rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50 ${STATUS_STYLE[inv.status] || "bg-surface-container-highest text-on-surface-variant"}`}
                      >
                        {(INVOICE_STATUSES.includes(inv.status) ? INVOICE_STATUSES : [inv.status, ...INVOICE_STATUSES]).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => setView(inv)} title="View" className="text-outline hover:text-primary"><Eye size={16} /></button>
                        <button onClick={() => download(inv._id)} title="Download" className="text-outline hover:text-primary"><Download size={16} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-4 text-sm text-on-surface-variant">
          <span>Showing {filtered.length === 0 ? 0 : (page - 1) * PER_PAGE + 1} to {Math.min(filtered.length, page * PER_PAGE)} of {filtered.length} results</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg border border-outline-variant inline-flex items-center gap-1 disabled:opacity-40 hover:bg-surface-container-low"><ChevronLeft size={14} /> Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-outline-variant inline-flex items-center gap-1 disabled:opacity-40 hover:bg-surface-container-low">Next <ChevronRight size={14} /></button>
          </div>
        </div>
      </div>

      {view && <InvoiceViewModal inv={view} onClose={() => setView(null)} onDownload={() => download(view._id)} />}
      {showSummary && <ProjectSummaryModal projects={projects} invoices={invoices} onClose={() => setShowSummary(false)} />}
    </div>
  );
}

// Per-project financial overview: value, progress, milestones + ETAs, paid vs
// outstanding — one place to read a project's whole financial position.
const MS_STYLE: Record<string, string> = {
  Released: "bg-secondary-container text-on-secondary-container",
  "In Escrow": "bg-amber-100 text-amber-700",
  Pending: "bg-surface-container-highest text-on-surface-variant",
};
function ProjectSummaryModal({ projects, invoices, onClose }: { projects: any[]; invoices: Invoice[]; onClose: () => void }) {
  const rows = projects.map((p) => {
    const totalValue = (p.projectPrice && p.projectPrice > 0) ? p.projectPrice : (p.escrowAmount || 0) + (p.releasedPayment || 0);
    const ms: any[] = p.milestones || [];
    const releasedMs = ms.filter((m) => m.status === "Released");
    const paid = p.releasedPayment || releasedMs.reduce((s, m) => s + (m.amount || 0), 0);
    const outstanding = Math.max(0, totalValue - paid);
    const progress = totalValue ? Math.min(100, Math.round((paid / totalValue) * 100)) : (ms.length ? Math.round((releasedMs.length / ms.length) * 100) : 0);
    const projInv = invoices.filter((i) => (i.project as any)?._id === p._id);
    return { p, totalValue, paid, outstanding, progress, ms, invCount: projInv.length };
  }).filter((r) => r.totalValue > 0 || r.ms.length > 0 || r.invCount > 0);

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant">
          <h2 className="text-lg font-bold text-on-surface">Project Financial Summary</h2>
          <button onClick={onClose} className="p-1.5 text-outline hover:bg-surface-container rounded-full"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          {rows.length === 0 ? (
            <p className="text-center py-10 text-on-surface-variant text-sm">No project financial data yet.</p>
          ) : rows.map(({ p, totalValue, paid, outstanding, progress, ms, invCount }) => (
            <div key={p._id} className="border border-outline-variant rounded-xl p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-bold text-on-surface truncate">{p.name}</div>
                  <div className="text-[12px] text-on-surface-variant">{p.assignedContact?.name || "—"} · {p.projectStatus || "—"} · {invCount} invoice{invCount === 1 ? "" : "s"}</div>
                </div>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-primary-container text-primary shrink-0">{money(totalValue)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2 text-center">
                <div className="rounded-lg bg-surface-container-low p-2"><div className="text-[10px] uppercase text-outline">Paid</div><div className="text-sm font-bold text-secondary">{money(paid)}</div></div>
                <div className="rounded-lg bg-surface-container-low p-2"><div className="text-[10px] uppercase text-outline">Outstanding</div><div className="text-sm font-bold text-error">{money(outstanding)}</div></div>
                <div className="rounded-lg bg-surface-container-low p-2"><div className="text-[10px] uppercase text-outline">Remaining</div><div className="text-sm font-bold text-on-surface">{money(Math.max(0, totalValue - paid))}</div></div>
              </div>
              <div className="mb-3">
                <div className="flex justify-between text-[11px] text-on-surface-variant mb-1"><span>Progress</span><span>{progress}%</span></div>
                <div className="h-2 w-full bg-surface-container-high rounded-full overflow-hidden"><div className="h-full bg-primary" style={{ width: `${progress}%` }} /></div>
              </div>
              {ms.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px] min-w-[420px]">
                    <thead><tr className="text-outline text-left"><th className="py-1 font-semibold">Milestone</th><th className="py-1 font-semibold text-right">Amount</th><th className="py-1 font-semibold">ETA</th><th className="py-1 font-semibold">Status</th></tr></thead>
                    <tbody>
                      {ms.map((m, i) => (
                        <tr key={i} className="border-t border-outline-variant/60">
                          <td className="py-1.5 text-on-surface">{m.title}</td>
                          <td className="py-1.5 text-right text-on-surface">{money(m.amount || 0)}</td>
                          <td className="py-1.5 text-on-surface-variant">{m.dueDate ? fmtDate(m.dueDate) : "—"}</td>
                          <td className="py-1.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${MS_STYLE[m.status] || ""}`}>{m.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Tiny inline sparkline for the Paid Invoices card (last-6-months collected).
function Sparkline({ data }: { data: number[] }) {
  const W = 96, H = 40;
  const max = Math.max(1, ...data);
  const step = data.length > 1 ? W / (data.length - 1) : W;
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(H - (v / max) * (H - 6) - 3).toFixed(1)}`);
  if (!data.some((v) => v > 0)) return <div className="w-24 h-10" />;
  return (
    <svg width={W} height={H} className="overflow-visible">
      <polyline points={pts.join(" ")} fill="none" stroke="#006a61" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <polygon points={`0,${H} ${pts.join(" ")} ${W},${H}`} fill="#006a61" opacity="0.08" />
    </svg>
  );
}

function InvoiceViewModal({ inv, onClose, onDownload }: { inv: Invoice; onClose: () => void; onDownload: () => void }) {
  const c: any = inv.client, p: any = inv.project;
  const row = (label: string, val: any) => (
    <div className="flex justify-between py-1.5 border-b border-outline-variant last:border-0"><span className="text-on-surface-variant">{label}</span><span className="font-semibold text-on-surface text-right">{val || "—"}</span></div>
  );
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant">
          <div><h2 className="text-lg font-bold text-on-surface">{inv.invoiceNumber}</h2><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[inv.status] || ""}`}>{inv.status}</span></div>
          <button onClick={onClose} className="p-1.5 text-outline hover:bg-surface-container rounded-full"><X size={18} /></button>
        </div>
        <div className="p-6 overflow-y-auto text-sm space-y-1">
          {row("Client", c?.name)}
          {row("Username", c?.username ? `@${c.username}` : "—")}
          {row("Project", p?.name)}
          {row("Milestone", inv.milestoneTitle)}
          {row("Type", (inv as any).projectSource)}
          {row("Amount", money(inv.amount, inv.currency))}
          {row("Payment Method", inv.paymentMethod)}
          {row("Invoice Date", fmtDate((inv as any).invoiceDate))}
          {row("Due Date", fmtDate(inv.dueDate))}
          {row("Last Updated", fmtDate(inv.updatedAt))}
        </div>
        <div className="px-6 py-4 border-t border-outline-variant flex justify-end gap-3">
          <button onClick={onDownload} className="inline-flex items-center gap-2 border border-outline-variant px-4 py-2 rounded-lg text-sm font-semibold hover:bg-surface-container-low"><Download size={16} /> Download</button>
          <button onClick={onClose} className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#004b9e]">Close</button>
        </div>
      </div>
    </div>
  );
}
